// Typed bindings over the generated OpenAPI client (./schema.ts).
//
// schema.ts is produced by `make openapi` (openapi-typescript, version pinned)
// and committed — never hand-edit it. These helpers layer end-to-end request /
// response types onto the shared fetch transport in services/api.ts, so the SPA
// and the Go API cannot drift: a contract change regenerates schema.ts and any
// mismatch surfaces as a TypeScript error here.

import { api } from '../services/api';
import type { components, paths } from './schema';

export type Schemas = components['schemas'];
export type HealthStatus = Schemas['HealthStatus'];
export type Me = Schemas['Me'];
export type CompanyMembership = Schemas['CompanyMembership'];
export type CompanyList = Schemas['CompanyList'];
export type CompanySwitchResult = Schemas['CompanySwitchResult'];
export type RBACContext = Schemas['RBACContext'];
export type RBACModuleContext = Schemas['RBACModuleContext'];
export type RBACRowScope = Schemas['RBACRowScope'];
export type SyncRequest = Schemas['SyncRequest'];
export type SyncResponse = Schemas['SyncResponse'];
export type SyncMutation = Schemas['SyncMutation'];
export type SyncResult = Schemas['SyncResult'];
// The proof-artifact surface (DATA-015) lives in services/proofUpload.ts — a
// lazy chunk — so its calls are not exported from here; the types are, like
// every other schema type.
export type TripProofMintRequest = Schemas['TripProofMintRequest'];
export type TripProofMint = Schemas['TripProofMint'];
export type TripProofURL = Schemas['TripProofURL'];

// The admin role/membership surface (FE-001). RoleRow is the editor's row — not
// Role — because a built-in or unedited seed role has no stored doc and the
// editor still lists it; see the RoleRow schema.
export type Role = Schemas['Role'];
export type RoleInput = Schemas['RoleInput'];
export type RoleRow = Schemas['RoleRow'];
export type RoleList = Schemas['RoleList'];
export type Membership = Schemas['Membership'];
export type MembershipInput = Schemas['MembershipInput'];
export type MemberRow = Schemas['MemberRow'];
export type MemberList = Schemas['MemberList'];
export type SubdivisionRef = Schemas['SubdivisionRef'];
// The assignment-picker option on the client axis (issue 545) — same shape as
// SubdivisionRef, distinct from the full Client doc the clients surface returns.
export type ClientRef = Schemas['ClientRef'];
export type FloorViolationError = Schemas['FloorViolationError'];

// The admin subdivision surface (API-002). Subdivision is the full doc the
// /admin/subdivisions screen renders (id/companyId/name/status), distinct from
// the SubdivisionRef assignment-picker option.
export type Subdivision = Schemas['Subdivision'];
export type SubdivisionList = Schemas['SubdivisionList'];

// The admin invite surface (AUTH-003). Invite is the invite doc the /admin/users
// invite list renders and every write echoes (the single-use token is never
// carried); Invite* are the request bodies for create/resend/revoke/accept.
export type Invite = Schemas['Invite'];
export type InviteList = Schemas['InviteList'];
export type InviteQuery = Schemas['InviteQuery'];
export type InviteCreate = Schemas['InviteCreate'];
export type InviteReason = Schemas['InviteReason'];
export type InviteAccept = Schemas['InviteAccept'];
export type InviteAcceptResult = Schemas['InviteAcceptResult'];

// The admin company surface (API-001). Company is the tenant-root doc the
// /admin/companies screen renders (id/name/status); CompanyAdminEntry adds the
// caller's role in each of its own companies (distinct from CompanyMembership,
// the pre-bind picker row).
export type Company = Schemas['Company'];
export type CompanyAdminEntry = Schemas['CompanyAdminEntry'];
export type CompanyAdminList = Schemas['CompanyAdminList'];

// Derived from the generated paths map (not the bare schema) so the binding
// breaks if the operation, status code, or media type ever changes.
type HealthzResponse = paths['/healthz']['get']['responses'][200]['content']['application/json'];

export function getHealthz(signal?: AbortSignal): Promise<HealthzResponse> {
  return api<HealthzResponse>('/healthz', { signal });
}

// postSync pushes a batch of queued offline mutations to the receiver,
// which resolves each last-write-wins and returns one outcome per mutation. The
// request/response types are pinned to the generated paths map, so a contract
// change surfaces here as a type error.
type SyncBody = paths['/api/sync']['post']['requestBody']['content']['application/json'];
type SyncResp = paths['/api/sync']['post']['responses'][200]['content']['application/json'];

export function postSync(body: SyncBody, signal?: AbortSignal): Promise<SyncResp> {
  return api<SyncResp>('/api/sync', { method: 'POST', body, signal });
}

// --- App-level health state, surfaced by the status pill (components/HealthPill). ---

export type HealthState =
  | { kind: 'unknown' }
  | { kind: 'ok'; health: HealthStatus }
  | { kind: 'degraded'; health: HealthStatus }
  | { kind: 'error' };

export const HEALTH_EVENT = 'app:health';
const FRESH_MS = 30_000;

let last: HealthState = { kind: 'unknown' };
let lastAt = 0;
let inflight: Promise<HealthState> | null = null;

export function getLastHealth(): HealthState {
  return last;
}

function publish(next: HealthState): HealthState {
  last = next;
  document.documentElement.dataset.health = next.kind;
  window.dispatchEvent(new CustomEvent<HealthState>(HEALTH_EVENT, { detail: next }));
  return next;
}

// Pings /healthz and broadcasts the result. Coalesces concurrent callers (one
// in-flight request) and serves a cached result within FRESH_MS so per-route
// remounts of the pill don't spam the endpoint. Pass force to bypass the cache.
export function checkHealth(force = false): Promise<HealthState> {
  if (inflight) return inflight;
  if (!force && last.kind !== 'unknown' && Date.now() - lastAt < FRESH_MS) {
    return Promise.resolve(last);
  }
  inflight = getHealthz()
    .then((health) =>
      publish(health.status === 'ok' ? { kind: 'ok', health } : { kind: 'degraded', health }),
    )
    .catch(() => publish({ kind: 'error' }))
    .finally(() => {
      lastAt = Date.now();
      inflight = null;
    });
  return inflight;
}
